import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button } from 'react-bootstrap';
import SimpleBar from 'simplebar-react';
import 'simplebar-react/dist/simplebar.min.css';
import { useTranslate } from '@tolgee/react';

import { INSTAGRAM } from '../../../../../config/constants';
import { PLATFORM_COLORS, PLATFORM_ICONS } from '../../../../../config/icons';
import { VIDEO } from '../../../config/constants';
import { lomavisCreatorRootState } from '../../../../../pages/lomavisCreator';
import {
  setModalUiContent,
  setPostDataAttribute
} from '../../../../../redux/slices/lomavisCreator/lomavisCreator';

import { EditorHeader } from '../../sharedComponents';
import { InstagramAudioTypeFilter, MusicSearch } from './search';
import { TrackList, AudioTrack } from './trackList';
import { TrackPreview } from './trackPreview';
import { useInstagramMusicControls } from './useInstagramMusicControls';

interface Props {
  handleClose: () => void;
}

/**
 * The video media item can carry either a raw local File/Blob (set right
 * after the user uploads from disk, before the server has resolved a URL) or
 * one of several string URLs. This returns either a usable URL string, or a
 * File/Blob that the caller turns into an object URL.
 */
const getVideoSource = (media: any[]): string | File | Blob | null => {
  const video = (media || []).find((m) => m?.media_type === VIDEO);
  if (!video) return null;

  // Local file reference (most common right after upload from disk).
  if (
    video.file &&
    (video.file instanceof File || video.file instanceof Blob)
  ) {
    return video.file;
  }
  // If `file` is already a URL string (e.g. when a media item is reloaded),
  // use it as-is.
  if (typeof video.file === 'string' && video.file) {
    return video.file;
  }

  // Server-hosted URLs in priority order, mirroring
  // InstagramPostPreview.getVideoUrl.
  return (
    video.video_url ||
    video.original_asset ||
    video.assetBlob ||
    video.preview_image ||
    null
  );
};

const InstagramMusic: FC<Props> = ({ handleClose }) => {
  const { t } = useTranslate();
  const dispatch = useDispatch();

  const postData = useSelector(
    (state: lomavisCreatorRootState) => state.lomavisCreator.postData[INSTAGRAM]
  );
  const existingMusic = postData?.additional_publication_data?.instagram_music;
  const { clearMusic } = useInstagramMusicControls(INSTAGRAM);

  // Resolve to a playable URL. If the media item carries a raw File/Blob
  // (right after a local upload), wrap it in an object URL and revoke it
  // when the source changes or the modal unmounts.
  const videoSource = useMemo(
    () => getVideoSource(postData?.media || []),
    [postData?.media]
  );

  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  useEffect(() => {
    if (videoSource === null) {
      setVideoUrl(null);
      return undefined;
    }
    if (typeof videoSource === 'string') {
      setVideoUrl(videoSource);
      return undefined;
    }
    const objectUrl = URL.createObjectURL(videoSource);
    setVideoUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [videoSource]);

  const [audioTypeFilter, setAudioTypeFilter] =
    useState<InstagramAudioTypeFilter>('all');
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<AudioTrack[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [selectedTrack, setSelectedTrack] = useState<AudioTrack | null>(
    existingMusic
      ? {
          audio_id: existingMusic.audio_id,
          audio_type: existingMusic.audio_type,
          title: existingMusic.title,
          display_artist: existingMusic.display_artist,
          duration_in_ms: existingMusic.duration_in_ms,
          cover_artwork_thumbnail_uri: existingMusic.cover_artwork_thumbnail_uri,
          download_url: existingMusic.download_url,
          ig_username: existingMusic.ig_username
        }
      : null
  );
  const [audioVolume, setAudioVolume] = useState<number>(
    existingMusic?.audio_volume ?? 100
  );
  const [videoVolume, setVideoVolume] = useState<number>(
    existingMusic?.video_volume ?? 100
  );

  const handleSelect = useCallback((track: AudioTrack) => {
    setSelectedTrack(track);
  }, []);

  const handleSave = () => {
    if (!selectedTrack) return;
    dispatch(
      setPostDataAttribute({
        platform: INSTAGRAM,
        attribute: 'additional_publication_data',
        value: {
          ...(postData?.additional_publication_data || {}),
          instagram_music: {
            audio_id: selectedTrack.audio_id,
            audio_type: selectedTrack.audio_type,
            title: selectedTrack.title,
            display_artist: selectedTrack.display_artist,
            duration_in_ms: selectedTrack.duration_in_ms,
            cover_artwork_thumbnail_uri:
              selectedTrack.cover_artwork_thumbnail_uri,
            download_url: selectedTrack.download_url ?? null,
            ig_username: selectedTrack.ig_username ?? null,
            audio_volume: audioVolume,
            video_volume: videoVolume
          }
        }
      })
    );
    handleClose();
  };

  const handleClear = () => {
    clearMusic();
    setSelectedTrack(null);
  };

  useEffect(() => {
    setSelectedTrack((current) =>
      current &&
      (audioTypeFilter === 'all' || current.audio_type === audioTypeFilter)
        ? current
        : null
    );
  }, [audioTypeFilter]);

  return (
    <div className="d-flex flex-column w-100 h-100 gap-4 overflow-hidden">
      {/* Header */}
      <EditorHeader>
        <div className="d-flex justify-content-between align-items-center flex-fill h-100 main_editor">
          <div className="d-flex align-items-center flex-fill h-100 gap-2">
            <i
              className={
                PLATFORM_ICONS[INSTAGRAM] + ' fs-1 d-flex align-items-center'
              }
              style={{ color: PLATFORM_COLORS[INSTAGRAM], fontSize: '32px' }}
            ></i>
            <h4 className="h-100 d-flex align-items-center mt-2">
              {t('lomavisCreator.IG.music.addMusic')}
            </h4>
          </div>
        </div>
      </EditorHeader>

      {/* Main */}
      <div
        style={{ minHeight: 0 }}
        className="d-flex flex-column w-100 gap-3 flex-grow-1 overflow-hidden px-3"
      >
        {errorCode === 'direct_connect_not_supported' && (
          <div className="alert alert-warning small mb-0">
            {t('lomavisCreator.IG.music.directConnectNotSupported')}
          </div>
        )}

        <div className="d-flex flex-row gap-3 flex-grow-1 overflow-hidden">
          <div
            style={{ flex: '2 1 0%', minWidth: 0 }}
            className="d-flex flex-column overflow-hidden bg-white rounded-3 p-3 gap-3"
          >
            <MusicSearch
              audioTypeFilter={audioTypeFilter}
              onAudioTypeFilterChange={setAudioTypeFilter}
              query={query}
              onQueryChange={setQuery}
              onResults={setResults}
              onLoadingChange={setIsLoading}
              onErrorChange={setErrorCode}
              audioVolume={audioVolume}
              videoVolume={videoVolume}
              onAudioVolumeChange={setAudioVolume}
              onVideoVolumeChange={setVideoVolume}
              hasSelectedTrack={Boolean(selectedTrack)}
              hasVideo={Boolean(videoUrl)}
            />

            <div className="flex-grow-1" style={{ minHeight: 0 }}>
              <SimpleBar
                forceVisible="y"
                autoHide={false}
                style={{ maxHeight: '100%' }}
              >
                <TrackList
                  tracks={results}
                  isLoading={isLoading}
                  selectedAudioId={selectedTrack?.audio_id}
                  onSelect={handleSelect}
                  onDeselect={() => setSelectedTrack(null)}
                />
              </SimpleBar>
            </div>
          </div>

          <div
            style={{ flex: '1 1 0%', minWidth: 0 }}
            className="d-flex flex-column justify-content-center overflow-auto bg-white rounded-3 p-3"
          >
            <TrackPreview
              track={selectedTrack}
              videoUrl={videoUrl}
              audioVolume={audioVolume}
              videoVolume={videoVolume}
              onAudioVolumeChange={setAudioVolume}
              onVideoVolumeChange={setVideoVolume}
            />
          </div>
        </div>
      </div>

      {/* Footer */}
      <div
        style={{ bottom: '0', zIndex: 100, height: '60px', flexShrink: 0 }}
        className="w-100 d-flex justify-content-between gap-3 rounded-3 align-items-center bg-white py-2 px-3 border-top h-60px"
      >
        <Button
          type="button"
          variant="outline-danger"
          size="sm"
          className="d-inline-flex align-items-center gap-2 px-3"
          onClick={handleClear}
          disabled={!existingMusic}
        >
          <i className="fa-duotone fa-trash"></i>
          {t('lomavisCreator.IG.music.clear')}
        </Button>
        <div className="d-flex justify-content-end gap-3 align-items-center">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => {
              dispatch(
                setModalUiContent({
                  modal: 'IGMusic',
                  attribute: 'toggle',
                  value: false
                })
              );
            }}
          >
            {t('lomavisCreator.back')}
          </Button>
          <Button
            type="button"
            variant="primary"
            size="sm"
            onClick={handleSave}
            disabled={!selectedTrack}
          >
            {t('lomavisCreator.buttons.save')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default InstagramMusic;
